import { SubPage } from 'app/components/CookieConsent/CookieConsent';
import AccessServiceTypes, { getAccessServiceDescription } from 'app/domain/AccessServiceTypes';
import { CookieConsentType } from '../../components/CookieConsent/CookieConsentType';
import ContentoAccessibilities from '../../domain/ContentoAccessibilities';
import { Accessibility } from '../../graphql/__generated__/types/contento';
import { ListTracking } from '../contentoUtils';
import { findAncestorWithHelixType, getListIndex, HELIX_TYPE_LIST } from '../helixUtils';
import { ImpressionEvent } from './clientStatistics';

interface TrackingEvent<T> {
    name: string;
    type?: string;
    content?: { id: string; type: string };
    eventValues: T;
}

type ListSearchEventValues = {
    listType: string;
    listId: string;
    listItemIndex: number;
    listIndex: number;
    contentId: string;
    searchTerm: string;
};

export const listSearchEvent = (
    listTracking: ListTracking,
    selectedItem: { listItemIndex: number; contentId: string },
    searchTerm: string
): TrackingEvent<ListSearchEventValues> => ({
    name: 'svtplay-search-autocomplete-click',
    type: 'search',
    eventValues: {
        ...listTracking,
        listItemIndex: selectedItem.listItemIndex,
        listIndex: 1,
        contentId: selectedItem.contentId,
        searchTerm,
    },
});

type SearchTermEventValues = {
    searchTerm: string;
};

export const searchQueryEvent = (searchTerm: string): TrackingEvent<SearchTermEventValues> => ({
    name: 'svtplay-search-query',
    type: 'search',
    eventValues: {
        searchTerm,
    },
});

export const searchShowAll = (searchTerm: string): TrackingEvent<SearchTermEventValues> => ({
    name: 'svtplay-search-show-all',
    eventValues: {
        searchTerm,
    },
});

type ListTrackingEventValues = {
    listType: string;
    listId: string;
    listItemIndex: number;
    listIndex: number;
    contentId: string;
    listExpandedState: string | boolean;
    leadsToPlayable: boolean;
    linkFromImage?: boolean;
};

type ListTrackingValues = {
    listType: string;
    listId: string;
    listItemIndex: number;
    contentId: string;
    listExpandedState?: string | boolean;
    leadsToPlayable?: boolean;
    linkFromImage?: boolean;
};

export const listTrackingEvent = (
    {
        listId,
        listType,
        listItemIndex,
        listExpandedState,
        contentId,
        leadsToPlayable = false,
        ...props
    }: ListTrackingValues,
    clickTarget = null
): TrackingEvent<ListTrackingEventValues> => {
    const list = findAncestorWithHelixType(clickTarget, HELIX_TYPE_LIST);

    const listIndex = getListIndex(list);

    let expandedState = '';

    if (!listExpandedState && list) {
        const expandButton = list.querySelector('[data-rt="expand-button"]');

        if (expandButton) {
            expandedState = expandButton.getAttribute('aria-expanded') === 'true' ? 'expand' : 'collapse';
        }
    }

    return {
        name: 'svtplay-list-tracking',
        eventValues: {
            listId,
            listType,
            listItemIndex,
            listIndex,
            listExpandedState: listExpandedState ? listExpandedState : expandedState,
            contentId,
            leadsToPlayable,
            ...props,
        },
    };
};

type ImpressionEventData = {
    listId: string;
    listType: string;
    overrideListIndex: number;
    listItemIndex: number;
    contentId: string;
    dateTime: string;
};

export const impressionEventPart = (
    { listId, listType, overrideListIndex, listItemIndex, contentId, dateTime }: ImpressionEventData,
    clickTarget = null
): ImpressionEvent => {
    const list = findAncestorWithHelixType(clickTarget, HELIX_TYPE_LIST);
    let listIndex = overrideListIndex;
    if (listIndex === undefined) {
        const lists = document.querySelectorAll(`[data-helix-type="${HELIX_TYPE_LIST}"]`);
        listIndex = Array.from(lists).indexOf(list);
        listIndex = listIndex > -1 ? listIndex + 1 : -1;
    }

    let listExpandedState = null;

    if (!listExpandedState && list) {
        const expandButton = list.querySelector('[data-rt="expand-button"]');

        if (expandButton) {
            listExpandedState = expandButton.getAttribute('aria-expanded') === 'true' ? 'expand' : 'collapse';
        }
    }

    return {
        listId,
        listType,
        listItemIndex,
        listIndex,
        listExpandedState,
        contentId,
        dateTime,
    };
};

export const impressionEventBatch = (
    impressionEvents: Array<ImpressionEvent>
): TrackingEvent<{ impressionEvents: Array<ImpressionEvent> }> => ({
    name: 'svtplay-impression-batched',
    eventValues: {
        impressionEvents,
    },
});

export const programGuideChangeDateEvent = (date: string): TrackingEvent<{ date: string }> => ({
    name: 'svtplay-program-guide-change-date',
    eventValues: {
        date,
    },
});

export const programGuideViewChannelEvent = (channel: string): TrackingEvent<{ channel: string }> => ({
    name: 'svtplay-program-guide-view-channel',
    eventValues: {
        channel,
    },
});

type ProgramGuideExpandItemEventValues = {
    svtId: string;
    start: string;
    channel: string;
};

export const programGuideExpandItem = (
    svtId: string,
    start: string,
    channel: string
): TrackingEvent<ProgramGuideExpandItemEventValues> => ({
    name: 'svtplay-program-guide-expand-item',
    eventValues: {
        svtId,
        start,
        channel,
    },
});

type MainMenuItemClickEventValues = {
    name: string;
    index: number;
};

export const mainMenuItemClickEvent = (
    name: string,
    index: number
): TrackingEvent<MainMenuItemClickEventValues> => ({
    name: 'svtplay-main-menu-item-click',
    eventValues: {
        name,
        index,
    },
});

type HighlightClickEvent = {
    interactionComponent: string;
    highlightName: string;
};

export const highlightClickEvent = (svtId: string, name: string): TrackingEvent<HighlightClickEvent> => ({
    name: 'one-view-interaction',
    type: 'interaction',
    content: { id: svtId, type: 'video' },
    eventValues: {
        interactionComponent: 'highlights-list',
        highlightName: name,
    },
});

export const footerClickEvent = (name: string): TrackingEvent<{ name: string }> => ({
    name: 'svtplay-footer-click',
    eventValues: {
        name,
    },
});

export const externalTeaser = (index: number): TrackingEvent<{ listItemIndex: number }> => ({
    name: 'svtplay-external-teaser',
    eventValues: {
        listItemIndex: index,
    },
});

export const externalArticle = (href: string): TrackingEvent<{ href: string }> => ({
    name: 'svtplay-external-article',
    eventValues: {
        href,
    },
});

export const genreGridItemClickEvent = (genre: string): TrackingEvent<{ genre: string }> => ({
    name: 'svtplay-genre-grid-item-click',
    eventValues: {
        genre,
    },
});

type LanguageSupportClickEventValues = {
    type: string;
    leadsToPlayable: boolean;
};

export const languageSupportClick = (
    type: AccessServiceTypes | ContentoAccessibilities | Accessibility,
    leadsToPlayable = false
): TrackingEvent<LanguageSupportClickEventValues> => ({
    name: 'svtplay-language-support-click',
    eventValues: {
        type: getAccessServiceDescription(type),
        leadsToPlayable,
    },
});

export const showMorePrograms = (channel: string): TrackingEvent<{ channel: string }> => ({
    name: 'svtplay-program-page-show-more',
    eventValues: {
        channel,
    },
});

export const programFilterClick = (filterName: string): TrackingEvent<{ filterName: string }> => ({
    name: 'svtplay-program-page-filter',
    eventValues: {
        filterName,
    },
});

export const programPageAlphabeticList = (letter: string): TrackingEvent<{ letter: string }> => ({
    name: 'svtplay-program-page-alphabetic-list',
    eventValues: {
        letter,
    },
});

export const programPageFilterAbroad = (toggleFilter: boolean): TrackingEvent<{ filterAbroad: string }> => ({
    name: 'svtplay-program-page-filter-abroad',
    eventValues: {
        filterAbroad: toggleFilter ? 'checked' : 'unchecked',
    },
});

export const programPageBackToNavigation = (letter: string): TrackingEvent<{ section: string }> => ({
    name: 'svtplay-program-page-back-to-navigation',
    eventValues: {
        section: letter,
    },
});

export const displayWindowVideo = (
    type: 'play-icon' | 'pause' | 'fullscreen' | 'play-fullscreen'
): TrackingEvent<{ type: string }> => ({
    name: 'svtplay-display-window-video',
    eventValues: {
        type,
    },
});

type PlayEventValues = {
    videoId: string;
    isSmartStart?: boolean;
    seasonName?: string;
};

export const titlePlayFromBeginning = (videoId: string): TrackingEvent<PlayEventValues> => ({
    name: 'svtplay-title-play-from-beginning',
    eventValues: {
        videoId,
    },
});

export const titlePlayTrailer = (videoId: string): TrackingEvent<PlayEventValues> => ({
    name: 'svtplay-title-play-trailer',
    eventValues: {
        videoId,
    },
});

export const titlePlay = (videoId: string, isSmartStart: boolean): TrackingEvent<PlayEventValues> => ({
    name: 'svtplay-title-play',
    eventValues: {
        videoId,
        isSmartStart,
    },
});

export const titlePlayExpandSeason = (name: string, videoId: string): TrackingEvent<PlayEventValues> => ({
    name: 'svtplay-title-expand-list',
    eventValues: {
        seasonName: name,
        videoId,
    },
});

export const titlePlayShowAllUpcoming = (videoId: string): TrackingEvent<PlayEventValues> => ({
    name: 'svtplay-title-show-all-upcoming',
    eventValues: {
        videoId,
    },
});

type ExpandListClickEventValues = {
    listId: string;
    action: string;
};

export const expandListClick = (
    listId: string,
    action: string
): TrackingEvent<ExpandListClickEventValues> => ({
    name: 'svtplay-expand-list',
    eventValues: {
        listId,
        action,
    },
});

interface SetSettingEventValues {
    interactionComponent: string;
    [key: string]: string;
}

const setSetting = (name: string, key: string, value: string): TrackingEvent<SetSettingEventValues> => ({
    name,
    type: 'one-view-interaction',
    eventValues: {
        interactionComponent: 'settings',
        [key]: value ? 'on' : 'off',
    },
});

export const trackTTFF = (time: string): TrackingEvent<{ time: string }> => ({
    name: 'svtplay-time-to-first-frame',
    eventValues: {
        time,
    },
});

type SeasonPickerEventValues = {
    interactionComponent: string;
    season: string;
};

export const seasonPicker = (season: string): TrackingEvent<SeasonPickerEventValues> => ({
    name: 'svtplay-season-picker',
    type: 'one-view-interaction',
    eventValues: {
        interactionComponent: 'player',
        season,
    },
});

type ToggleEpisodePickerEventValues = {
    interactionComponent: string;
    episodePicker: string;
    playedPercentage: number;
};

export const toggleEpisodePicker = (
    value: boolean,
    playedPercentage: number
): TrackingEvent<ToggleEpisodePickerEventValues> => ({
    name: 'svtplay-episode-picker',
    type: 'one-view-interaction',
    eventValues: {
        interactionComponent: 'player',
        episodePicker: value ? 'expanded' : 'hidden',
        playedPercentage,
    },
});

export const trackModalBack = (): TrackingEvent<{ name: string }> => ({
    name: 'svtplay-modal-click',
    eventValues: {
        name: 'back',
    },
});

export const trackModalTitle = (svtId: string): TrackingEvent<{ title: string }> => ({
    name: 'svtplay-modal-click',
    eventValues: {
        title: svtId,
    },
});

export const trackModalEpisode = (svtId: string): TrackingEvent<{ episode: string }> => ({
    name: 'svtplay-modal-click',
    eventValues: {
        episode: svtId,
    },
});

export const trackModalAccessibilityVariant = (
    variant: AccessServiceTypes | ContentoAccessibilities | Accessibility
): TrackingEvent<{ variant: string }> => ({
    name: 'svtplay-modal-click',
    eventValues: {
        variant: getAccessServiceDescription(variant),
    },
});

type ModalNextEventValues = {
    type: string;
    svtId: string;
};

export const trackModalNextEpisodeButton = (
    type: string,
    svtId: string = null
): TrackingEvent<ModalNextEventValues> => ({
    name: 'svtplay-modal-next-click',
    eventValues: {
        type,
        svtId,
    },
});

export const trackModalNextExposure = (type, svtId = null): TrackingEvent<ModalNextEventValues> => ({
    name: 'svtplay-modal-next-exposure',
    eventValues: {
        type,
        svtId,
    },
});

type FavoriteEventValues = {
    favorite_add?: string; // eslint-disable-line
    favorite_remove?: string; // eslint-disable-line
};

export const trackFavorite = (titleId: string, favoriteAdd = true): TrackingEvent<FavoriteEventValues> => {
    const eventValues: FavoriteEventValues = {};

    if (favoriteAdd) {
        eventValues.favorite_add = titleId;
    } else {
        eventValues.favorite_remove = titleId;
    }

    return {
        name: 'svtplay-favorite-tracking',
        eventValues,
    };
};

type UsabilityTestEventValues = {
    task: string;
    action: string;
    expectedEase?: number;
    experiencedEase?: number;
};

export enum USER_EVENT_TYPE {
    ACCEPTED = 'accepted',
    START = 'start',
    CANCEL = 'cancel',
    DONE = 'done',
    EXPERIENCED = 'experienced',
}

export const trackUsabilityTest = (testData: {
    type: USER_EVENT_TYPE;
    task: string;
    value?: number;
}): TrackingEvent<UsabilityTestEventValues> => {
    const eventValues: UsabilityTestEventValues = {
        task: testData.task,
        action: testData.type,
    };

    switch (testData.type) {
        case USER_EVENT_TYPE.START:
            eventValues.expectedEase = testData.value;
            break;
        case USER_EVENT_TYPE.EXPERIENCED:
            eventValues.experiencedEase = testData.value;
            break;
        default:
            break;
    }

    return {
        name: 'svtplay-usability-test',
        eventValues,
    };
};

export enum CookieConsentAction {
    ACCEPT_ALL = 'accept_all',
    REJECT_ALL = 'reject_all',
    CUSTOM = 'custom',
    CONFIRM_ACCEPT = 'confirm_accept',
    CONFIRM_REJECT = 'confirm_reject',
}

type CookieConsentEventValues = {
    subpage: SubPage;
    action: CookieConsentAction;
    [CookieConsentType.FUNCTIONALITY]?: boolean;
    [CookieConsentType.PERSONALIZATION]?: boolean;
    [CookieConsentType.REQUIRED]?: boolean;
    [CookieConsentType.SECURITY]?: boolean;
    [CookieConsentType.STATISTICS]?: boolean;
};

export const trackCookieConsentAction = (
    subpage: SubPage,
    action: CookieConsentAction,
    consents: { [key in CookieConsentType]?: boolean }
): TrackingEvent<CookieConsentEventValues> => ({
    name: 'cookie-consents',
    eventValues: {
        subpage,
        action,
        ...consents,
    },
});

export const trackShowCookieConsent = (): { name: string } => ({
    name: 'svtplay-show-coookie-consent-modal',
});

export const trackNavigateToCategory = (): { name: string } => ({
    name: 'svtplay-navigate-to-programs-page',
});

export const setAutoplayNext = (value: string): TrackingEvent<SetSettingEventValues> =>
    setSetting('set-autoplay-next', 'autoplayNext', value);

export const setVideoCompatibilityMode = (value: string): TrackingEvent<SetSettingEventValues> =>
    setSetting('set-video-compatibility-mode', 'videoCompatibilityMode', value);

export const setContinueWatching = (value: string): TrackingEvent<SetSettingEventValues> =>
    setSetting('set-continue-watching', 'continueWatching', value);

export const setEnhancedParentalLock = (value: string): TrackingEvent<SetSettingEventValues> =>
    setSetting('set-enhanced-parental-lock', 'enhancedParentalLock', value);
